// Dealership brand color configurations
// Each dealership has primary, secondary, and accent colors

export const dealershipThemes = {
  'Shell': {
    name: 'Shell',
    primary: '#ED1C24',      // Shell Red
    secondary: '#FFD500',    // Shell Gold/Yellow
    accent: '#FFD500',
    headerBg: 'linear-gradient(135deg, #ED1C24 0%, #B81820 100%)',
    sidebarBg: 'linear-gradient(180deg, #ED1C24 0%, #B81820 100%)',
    buttonBg: '#ED1C24',
    buttonHover: '#B81820',
    badgeBg: '#FFD500',
    badgeText: '#1a1a1a',
    chartColors: ['#ED1C24', '#FFD500', '#B81820', '#FFA500', '#8B0000']
  },
  'Nayara': {
    name: 'Nayara',
    primary: '#0072BC',      // Dark Blue (PANTONE-7686 C approximation)
    secondary: '#00A9E0',    // Light Blue (PANTONE-638 C approximation)
    accent: '#00A9E0',
    headerBg: 'linear-gradient(135deg, #0072BC 0%, #005A94 100%)',
    sidebarBg: 'linear-gradient(180deg, #0072BC 0%, #004A7C 100%)',
    buttonBg: '#0072BC',
    buttonHover: '#005A94',
    badgeBg: '#00A9E0',
    badgeText: '#ffffff',
    chartColors: ['#0072BC', '#00A9E0', '#005A94', '#4DB8E8', '#003D66']
  },
  'HPCL': {
    name: 'HPCL',
    primary: '#CE0000',      // Russian Red (Monza)
    secondary: '#195AAD',    // Fun Blue
    accent: '#F78222',       // Ecstasy Orange
    headerBg: 'linear-gradient(135deg, #CE0000 0%, #A00000 100%)',
    sidebarBg: 'linear-gradient(180deg, #00006B 0%, #000050 100%)',
    buttonBg: '#CE0000',
    buttonHover: '#A00000',
    badgeBg: '#F78222',
    badgeText: '#ffffff',
    chartColors: ['#CE0000', '#195AAD', '#F78222', '#00006B', '#FF6B6B']
  },
  'BPCL': {
    name: 'BPCL',
    primary: '#007BC9',      // Lochmara Blue
    secondary: '#FFE000',    // Golden Yellow
    accent: '#FFE000',
    headerBg: 'linear-gradient(135deg, #007BC9 0%, #0062A0 100%)',
    sidebarBg: 'linear-gradient(180deg, #007BC9 0%, #005080 100%)',
    buttonBg: '#007BC9',
    buttonHover: '#0062A0',
    badgeBg: '#FFE000',
    badgeText: '#1a1a1a',
    chartColors: ['#007BC9', '#FFE000', '#0062A0', '#4DA6DB', '#004060']
  },
  'IOCL': {
    name: 'IOCL',
    primary: '#F37022',      // Saffron Orange
    secondary: '#02164F',    // Dark Blue
    accent: '#F37022',
    headerBg: 'linear-gradient(135deg, #02164F 0%, #010D30 100%)',
    sidebarBg: 'linear-gradient(180deg, #02164F 0%, #010D30 100%)',
    buttonBg: '#F37022',
    buttonHover: '#D65A10',
    badgeBg: '#F37022',
    badgeText: '#ffffff',
    chartColors: ['#F37022', '#02164F', '#D65A10', '#4A5D8F', '#FF8C42']
  },
  'Jio-BP': {
    name: 'Jio-BP',
    primary: '#009B00',      // Islam Green (BP Heritage)
    secondary: '#AFCA08',    // Rio Grande Green
    accent: '#FFFF00',       // Digital Yellow
    headerBg: 'linear-gradient(135deg, #009B00 0%, #007800 100%)',
    sidebarBg: 'linear-gradient(180deg, #009B00 0%, #006400 100%)',
    buttonBg: '#009B00',
    buttonHover: '#007800',
    badgeBg: '#AFCA08',
    badgeText: '#1a1a1a',
    chartColors: ['#009B00', '#AFCA08', '#FFFF00', '#007800', '#6B8E23']
  },
  'Reliance Petroleum': {
    name: 'Reliance Petroleum',
    primary: '#0070B4',      // Honolulu Blue
    secondary: '#D7AF64',    // Broken Yellow/Golden
    accent: '#D7AF64',
    headerBg: 'linear-gradient(135deg, #0070B4 0%, #005890 100%)',
    sidebarBg: 'linear-gradient(180deg, #0070B4 0%, #004570 100%)',
    buttonBg: '#0070B4',
    buttonHover: '#005890',
    badgeBg: '#D7AF64',
    badgeText: '#1a1a1a',
    chartColors: ['#0070B4', '#D7AF64', '#005890', '#4A9AC9', '#003050']
  }
};

// Default theme (Fuelogic branded) - used when no dealership selected
export const defaultTheme = {
  name: 'Fuelogic',
  primary: '#0066FF',        // Fuelogic Blue
  secondary: '#00CED1',      // Fuelogic Teal
  accent: '#00CED1',
  headerBg: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
  sidebarBg: 'linear-gradient(180deg, #0066FF 0%, #0040A0 100%)',
  buttonBg: '#0066FF',
  buttonHover: '#0052CC',
  badgeBg: '#00CED1',
  badgeText: '#1a1a1a',
  chartColors: ['#0066FF', '#00CED1', '#0052CC', '#4D94FF', '#003080']
};

// List of all dealerships for dropdowns
export const dealershipList = [
  'BPCL',
  'HPCL', 
  'IOCL',
  'Jio-BP',
  'Nayara',
  'Reliance Petroleum',
  'Shell'
];

// Get theme by dealership name
export const getThemeByDealership = (dealership) => {
  if (!dealership) return defaultTheme;
  
  // Handle legacy/alternate names
  const normalizedName = dealership.trim();
  
  // Map legacy names to current names
  const nameMap = {
    'Nayra': 'Nayara',
    'Reliance': 'Reliance Petroleum',
    'IOL': 'IOCL',
    'Jio': 'Jio-BP'
  };
  
  const mappedName = nameMap[normalizedName] || normalizedName;
  
  return dealershipThemes[mappedName] || defaultTheme;
};

// Generate CSS variables from theme
export const generateThemeCSS = (theme) => {
  return {
    '--dealership-primary': theme.primary,
    '--dealership-secondary': theme.secondary,
    '--dealership-accent': theme.accent,
    '--dealership-header-bg': theme.headerBg,
    '--dealership-sidebar-bg': theme.sidebarBg,
    '--dealership-button-bg': theme.buttonBg,
    '--dealership-button-hover': theme.buttonHover,
    '--dealership-badge-bg': theme.badgeBg,
    '--dealership-badge-text': theme.badgeText
  };
};
